import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Copy } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import { ApiError } from '../../../lib/api-client.js';
import { Button } from '../../../shared/components/ui/Button.js';
import { Input } from '../../../shared/components/ui/Input.js';
import { useAuthStore } from '../../../stores/auth-store.js';
import { confirmSetupTotp, getSetupTotp } from '../api.js';

export function SetupTotpPage() {
  const [code, setCode] = useState('');
  const navigate = useNavigate();
  const pendingToken = useAuthStore((s) => s.pendingToken);
  const setUser = useAuthStore((s) => s.setUser);
  const setPendingToken = useAuthStore((s) => s.setPendingToken);

  useEffect(() => {
    if (!pendingToken) {
      void navigate({ to: '/login' });
    }
  }, [pendingToken, navigate]);

  const setupQuery = useQuery({
    queryKey: ['setup-totp', pendingToken],
    queryFn: async () => getSetupTotp(pendingToken ?? ''),
    enabled: !!pendingToken,
    refetchOnMount: 'always',
    staleTime: Infinity,
  });

  const confirmMut = useMutation({
    mutationFn: async () => confirmSetupTotp(pendingToken ?? '', code),
    onSuccess: (data) => {
      setUser(data.user);
      setPendingToken(null);
      toast.success('TOTP configurado com sucesso!');
      void navigate({ to: '/' });
    },
    onError: (err) => {
      if (err instanceof ApiError) toast.error(err.problem.title, { description: err.problem.detail });
    },
  });

  const copySecret = async (): Promise<void> => {
    if (!setupQuery.data) return;
    await navigator.clipboard.writeText(setupQuery.data.secret);
    toast.success('Secret copiado');
  };

  if (!pendingToken) return null;

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#f8fafc] p-4">
      <div className="w-full max-w-[400px]">
        <div className="mb-8 flex flex-col items-center">
          <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-[#0a4ee3] shadow-lg">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M3 17l4-8 4 4 4-6 4 5"
                stroke="white"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path d="M3 21h18" stroke="white" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </div>
          <h1 className="text-xl font-bold tracking-tight text-[#0f172a]">MetricsFarma</h1>
          <p className="mt-1 text-sm text-[#64748b]">Inteligência de vendas para farmácias</p>
        </div>

        <form
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            confirmMut.mutate();
          }}
          className="rounded-2xl border border-[#e2e8f0] bg-white p-8 shadow-sm"
        >
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-[#0f172a]">Configurar TOTP</h2>
            <p className="mt-1 text-sm text-[#64748b]">
              Use Google Authenticator, Authy ou 1Password
            </p>
          </div>

          {setupQuery.isLoading && (
            <div className="py-12 text-center text-sm text-[#94a3b8]">Gerando QR code...</div>
          )}

          {setupQuery.data && (
            <>
              <div className="mb-5 flex flex-col items-center gap-3">
                <img
                  src={setupQuery.data.qr_data_url}
                  alt="QR Code TOTP"
                  width={200}
                  height={200}
                  className="rounded-xl border border-[#e2e8f0]"
                />
                <div className="flex w-full items-center gap-2 rounded-lg border border-[#f1f5f9] bg-[#f8fafc] px-3 py-2">
                  <code className="flex-1 break-all font-mono text-[11px] text-[#0f172a]">
                    {setupQuery.data.secret}
                  </code>
                  <button
                    type="button"
                    onClick={copySecret}
                    className="rounded-lg p-1.5 text-[#94a3b8] transition-colors hover:bg-[#f1f5f9] hover:text-[#64748b]"
                    title="Copiar secret"
                  >
                    <Copy size={14} />
                  </button>
                </div>
                <p className="text-center text-xs text-[#94a3b8]">
                  Escaneie o QR ou cole o secret no app. Depois digite o código abaixo.
                </p>
              </div>

              <div className="flex flex-col gap-4">
                <Input
                  label="Código gerado pelo app"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  pattern="\d{6}"
                  autoComplete="one-time-code"
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  autoFocus
                  className="text-center font-mono text-[18px] tracking-[0.5em]"
                />
                <Button type="submit" size="lg" className="mt-1 w-full" loading={confirmMut.isPending}>
                  Confirmar
                </Button>
              </div>
            </>
          )}
        </form>

        <p className="mt-6 text-center text-xs text-[#94a3b8]">
          © 2026 MetricsFarma · Todos os direitos reservados
        </p>
      </div>
    </div>
  );
}
